import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { Deal, dealsAPI, Company, companiesAPI, Quote, quotesAPI, PaymentStatus, ShippingStatus, FulfillmentPriority, pickingTasksAPI, OrderDocument } from '../../services/api';
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  CurrencyDollarIcon,
  UserIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  DocumentTextIcon,
  ShoppingCartIcon,
  DocumentArrowDownIcon,
  DocumentArrowUpIcon,
  FunnelIcon,
  ChartBarIcon,
  DocumentDuplicateIcon,
  PlusIcon,
  EyeIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  TruckIcon,
  BanknotesIcon,
  ClipboardDocumentListIcon,
  CubeIcon,
} from '@heroicons/react/24/outline';
import QuoteModal from './QuoteModal';
import QuoteDetailModal from './QuoteDetailModal';
import QuoteSendModal from './QuoteSendModal';
import OrderReceiptModal from './OrderReceiptModal';
import ShippingTrackingModal from './ShippingTrackingModal';
import TaskList from '../tasks/TaskList';
import TaskFormModal from '../tasks/TaskFormModal';
import ProblemsSection from './ProblemsSection';
import InvoiceGeneratorModal, { type InvoiceGenerationResult } from '../invoices/InvoiceGeneratorModal';
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Badge,
  Avatar,
  Spinner,
  EmptyState,
} from '../ui';

const DealDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [deal, setDeal] = useState<Deal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [message, setMessage] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [orderDocuments, setOrderDocuments] = useState<OrderDocument[]>([]);
  const [showPoUploadForm, setShowPoUploadForm] = useState(false);
  const [dealPoFile, setDealPoFile] = useState<File | null>(null);
  const [dealPoName, setDealPoName] = useState('');
  const [dealPoDescription, setDealPoDescription] = useState('');
  const [uploadingDealPo, setUploadingDealPo] = useState(false);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState<number | null>(null);
  const [quoteToSend, setQuoteToSend] = useState<Quote | null>(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showShippingModal, setShowShippingModal] = useState(false);

  // Tasks
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskRefreshKey, setTaskRefreshKey] = useState(0);

  // Picking Tasks
  const [generatingPickingTask, setGeneratingPickingTask] = useState(false);
  const [pickingTaskError, setPickingTaskError] = useState('');

  // Check if we're in admin context
  const isAdminContext = location.pathname.startsWith('/admin');

  // Helper function to generate correct paths
  const getDealPath = (path: string) => {
    return isAdminContext ? `/admin${path}` : path;
  };

  useEffect(() => {
    if (id) {
      fetchDeal();
      fetchQuotes();
      fetchOrderDocuments();
    }
  }, [id]);

  const fetchDeal = async () => {
    try {
      setLoading(true);
      const data = await dealsAPI.getById(Number(id));
      setDeal(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch deal');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuotes = async () => {
    try {
      setLoadingQuotes(true);
      const data = await quotesAPI.getAll({ dealId: Number(id) });
      setQuotes(data);
    } catch (err: any) {
      console.error('Failed to fetch quotes:', err);
    } finally {
      setLoadingQuotes(false);
    }
  };

  const handleDelete = async () => {
    try {
      await dealsAPI.delete(Number(id));
      navigate('/deals');
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Failed to delete deal');
    }
  };

  const handleGeneratePickingTask = async () => {
    if (!deal) return;
    try {
      setGeneratingPickingTask(true);
      setPickingTaskError('');
      await pickingTasksAPI.generate(deal.id);
      // Show success message
      alert('Picking task generated successfully! Go to Picking & Packing tab to view it.');
      // Optionally navigate to picking & packing tab
      // navigate(getDealPath('/deals?tab=picking'));
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to generate picking task';
      setPickingTaskError(errorMsg);
      alert(`Error: ${errorMsg}`);
    } finally {
      setGeneratingPickingTask(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const data = await companiesAPI.getAll();
      setCompanies(data.filter(c => c.isActive));
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Failed to fetch companies');
      throw err;
    }
  };

  const fetchOrderDocuments = async () => {
    if (!id) return;
    try {
      const documents = await dealsAPI.getDocuments(Number(id));
      setOrderDocuments(documents);
    } catch (err) {
      console.error('Failed to fetch order documents:', err);
    }
  };

  const handleUploadDealPo = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!dealPoFile) {
      setActionError('Please choose a purchase order file');
      return;
    }

    try {
      setUploadingDealPo(true);
      setActionError('');
      await dealsAPI.uploadDocument(Number(id), dealPoFile, {
        documentType: 'PURCHASE_ORDER',
        name: dealPoName.trim() || dealPoFile.name,
        description: dealPoDescription.trim() || undefined,
      });
      setDealPoFile(null);
      setDealPoName('');
      setDealPoDescription('');
      setShowPoUploadForm(false);
      await fetchOrderDocuments();
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Failed to upload customer PO');
    } finally {
      setUploadingDealPo(false);
    }
  };

  const handleDeleteOrderDocument = async (documentId: number) => {
    if (!deal || !window.confirm('Delete this customer PO?')) return;
    try {
      await dealsAPI.deleteDocument(deal.id, documentId);
      await fetchOrderDocuments();
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Failed to delete customer PO');
    }
  };

  const openInvoiceModal = async () => {
    try {
      setActionError('');
      await Promise.all([fetchCompanies(), fetchOrderDocuments()]);
      setShowInvoiceModal(true);
    } catch {
      // fetchCompanies reports the user-facing error.
    }
  };

  const handleInvoiceSuccess = async (result: InvoiceGenerationResult) => {
    setActionError('');
    setMessage(result.message);
    await fetchOrderDocuments();
    window.setTimeout(() => setMessage(''), result.action === 'email' ? 4000 : 3000);
  };

  const formatCurrency = (amount: string, currency: string) => {
    return `${currency} ${parseFloat(amount).toLocaleString()}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatStatus = (status?: string): string => {
    if (!status) return '-';
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getPaymentStatusColor = (status?: PaymentStatus): 'success' | 'warning' | 'error' | 'primary' | 'neutral' => {
    switch (status) {
      case 'PAID': return 'success';
      case 'PARTIAL': return 'warning';
      case 'PENDING': return 'primary';
      case 'OVERDUE': return 'error';
      case 'REFUNDED': return 'neutral';
      case 'DISPUTED': return 'error';
      case 'CANCELLED': return 'neutral';
      default: return 'neutral';
    }
  };

  const getShippingStatusColor = (status?: ShippingStatus): 'success' | 'warning' | 'error' | 'primary' | 'neutral' => {
    switch (status) {
      case 'DELIVERED': return 'success';
      case 'IN_TRANSIT': return 'primary';
      case 'OUT_FOR_DELIVERY': return 'primary';
      case 'PICKED_UP': return 'primary';
      case 'LABEL_CREATED': return 'warning';
      case 'NOT_SHIPPED': return 'neutral';
      case 'RETURNED': return 'error';
      case 'EXCEPTION': return 'error';
      default: return 'neutral';
    }
  };

  const getPriorityColor = (priority?: FulfillmentPriority): 'success' | 'warning' | 'error' | 'primary' | 'neutral' => {
    switch (priority) {
      case 'CRITICAL': return 'error';
      case 'RUSH': return 'warning';
      case 'EXPEDITED': return 'primary';
      case 'STANDARD': return 'neutral';
      case 'HOLD': return 'neutral';
      default: return 'neutral';
    }
  };

  const getQuoteStatusBadge = (status: string) => {
    const statusConfig = {
      DRAFT: { color: 'bg-neutral-100 text-neutral-800 border-neutral-200', icon: DocumentTextIcon, label: 'Draft' },
      SENT: { color: 'bg-primary-100 text-primary-800 border-primary-200', icon: PaperAirplaneIcon, label: 'Sent' },
      ACCEPTED: { color: 'bg-success-100 text-success-800 border-success-200', icon: CheckCircleIcon, label: 'Accepted' },
      REJECTED: { color: 'bg-error-100 text-error-800 border-error-200', icon: XCircleIcon, label: 'Rejected' },
      EXPIRED: { color: 'bg-warning-100 text-warning-800 border-warning-200', icon: ClockIcon, label: 'Expired' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.DRAFT;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <Card variant="bordered">
          <CardBody>
            <EmptyState
              type="error"
              title="Error Loading Deal"
              description={error}
              action={
                <Button variant="primary" onClick={() => window.location.reload()}>
                  Try Again
                </Button>
              }
            />
          </CardBody>
        </Card>
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <Card variant="bordered">
          <CardBody>
            <EmptyState
              type="deals"
              title="Deal not found"
              description="The deal you're looking for doesn't exist."
              action={
                <Link to={getDealPath("/deals")}>
                  <Button variant="primary">Back to Deals</Button>
                </Link>
              }
            />
          </CardBody>
        </Card>
      </div>
    );
  }

  const customerPurchaseOrders = orderDocuments.filter((document) => document.documentType === 'PURCHASE_ORDER');

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      {/* Breadcrumb */}
      <nav className="flex" aria-label="Breadcrumb">
        <ol className="flex items-center space-x-2 text-sm">
          <li>
            <Link
              to={getDealPath('/deals')}
              className="text-neutral-500 hover:text-neutral-700 transition-colors"
            >
              Orders
            </Link>
          </li>
          <li>
            <span className="text-neutral-400">/</span>
          </li>
          <li>
            <span className="text-neutral-900 font-medium">
              {deal.orderNumber || `Order #${deal.id}`}
            </span>
          </li>
        </ol>
      </nav>

      {/* Header Card */}
      <Card variant="gradient" className="overflow-hidden">
        <CardBody className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(getDealPath('/deals'))}
                className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
                title="Back to Orders"
              >
                <ArrowLeftIcon className="h-5 w-5 text-white" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-white">{deal.description}</h1>
                <p className="text-white/70 text-sm">
                  {deal.orderNumber || `Order #${deal.id}`}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button variant="ghost" onClick={openInvoiceModal} className="text-white hover:bg-white/20">
                <DocumentTextIcon className="h-4 w-4 mr-2" />
                Generate Invoice
              </Button>
              <Button variant="ghost" onClick={() => setShowReceiptModal(true)} className="text-white hover:bg-white/20">
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                Export Receipt
              </Button>
              {deal.orderItems && deal.orderItems.length > 0 && (
                <Button
                  variant="ghost"
                  onClick={handleGeneratePickingTask}
                  disabled={generatingPickingTask}
                  className="bg-white/20 text-white hover:bg-white/30 border border-white/30"
                >
                  <CubeIcon className="h-4 w-4 mr-2" />
                  {generatingPickingTask ? 'Generating...' : 'Generate Picking Task'}
                </Button>
              )}
              {deal.shippedAt || (deal.shippingStatus && !['NOT_SHIPPED', 'LABEL_CREATED'].includes(deal.shippingStatus)) ? (
                <Button variant="ghost" onClick={() => setShowShippingModal(true)} className="bg-white/20 text-white hover:bg-white/30 border border-white/30">
                  <TruckIcon className="h-4 w-4 mr-2" />
                  Edit Tracking
                </Button>
              ) : deal.stage?.isClosedWon ? (
                <Button variant="ghost" onClick={() => setShowShippingModal(true)} className="bg-white/20 text-white hover:bg-white/30 border border-white/30">
                  <TruckIcon className="h-4 w-4 mr-2" />
                  Ship Order
                </Button>
              ) : (
                <span className="rounded-xl border border-white/20 bg-white/10 px-3 py-2 text-xs font-semibold text-white/80">
                  Shipping available after deal is won
                </span>
              )}
              <Link to={getDealPath(`/deals/${deal.id}/edit`)}>
                <Button variant="primary">
                  <PencilIcon className="h-4 w-4 mr-2" />
                  Edit Order
                </Button>
              </Link>
              <Button variant="danger" onClick={() => setShowDeleteConfirm(true)}>
                <TrashIcon className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {actionError && (
        <div className="rounded-lg border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700">
          {actionError}
        </div>
      )}

      {message && (
        <div className="rounded-lg border border-success-200 bg-success-50 px-4 py-3 text-sm text-success-700">
          {message}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Deal Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Deal Overview */}
          <Card variant="bordered">
            <CardHeader title="Deal Overview" />
            <CardBody>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-neutral-500 flex items-center">
                    <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                    Amount
                  </dt>
                  <dd className="mt-1 text-lg font-semibold text-neutral-900">
                    {formatCurrency(deal.amount, deal.currency)}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-neutral-500 flex items-center">
                    <CalendarIcon className="h-4 w-4 mr-1" />
                    Created
                  </dt>
                  <dd className="mt-1 text-sm text-neutral-900">
                    {formatDate(deal.createdAt)}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-neutral-500 flex items-center">
                    <UserIcon className="h-4 w-4 mr-1" />
                    Salesperson
                  </dt>
                  <dd className="mt-1 text-sm text-neutral-900 flex items-center">
                    {deal.user?.firstName && deal.user?.lastName ? (
                      <>
                        <Avatar name={`${deal.user.firstName} ${deal.user.lastName}`} size="sm" className="mr-2" />
                        {deal.user.firstName} {deal.user.lastName}
                      </>
                    ) : (
                      deal.user?.email || 'Unknown'
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-neutral-500 flex items-center">
                    <CalendarIcon className="h-4 w-4 mr-1" />
                    Last Updated
                  </dt>
                  <dd className="mt-1 text-sm text-neutral-900">
                    {formatDate(deal.updatedAt)}
                  </dd>
                </div>
              </dl>
            </CardBody>
          </Card>

          {/* Pipeline & Stage Information */}
          {(deal.pipeline || deal.stage) && (
            <Card variant="bordered">
              <CardHeader
                title="Pipeline & Stage"
                subtitle="Current position in the sales pipeline"
                icon={<FunnelIcon className="h-5 w-5" />}
              />
              <CardBody>
                <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                  {deal.pipeline && (
                    <div>
                      <dt className="text-sm font-medium text-neutral-500">Pipeline</dt>
                      <dd className="mt-2 flex items-center space-x-2">
                        <Badge variant="primary">{deal.pipeline.name}</Badge>
                        {deal.pipeline.isDefault && (
                          <Badge variant="neutral" size="sm">Default</Badge>
                        )}
                      </dd>
                    </div>
                  )}
                  {deal.stage && (
                    <div>
                      <dt className="text-sm font-medium text-neutral-500">Current Stage</dt>
                      <dd className="mt-2">
                        <div className="flex items-center space-x-2">
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: deal.stage.color || '#6B7280' }}
                          />
                          <span className="text-sm font-semibold text-neutral-900">
                            {deal.stage.name}
                          </span>
                        </div>
                        {deal.stage.description && (
                          <p className="mt-1 text-sm text-neutral-500">{deal.stage.description}</p>
                        )}
                        <div className="mt-2 flex items-center space-x-2">
                          {deal.stage.isClosedWon && (
                            <Badge variant="success" size="sm">✓ Closed Won</Badge>
                          )}
                          {deal.stage.isClosedLost && (
                            <Badge variant="error" size="sm">✗ Closed Lost</Badge>
                          )}
                        </div>
                      </dd>
                    </div>
                  )}
                  {deal.probability !== null && deal.probability !== undefined && (
                    <div>
                      <dt className="text-sm font-medium text-neutral-500 flex items-center">
                        <ChartBarIcon className="h-4 w-4 mr-1" />
                        Win Probability
                      </dt>
                      <dd className="mt-2">
                        <div className="flex items-center space-x-3">
                          <div className="flex-1 bg-neutral-200 rounded-full h-3 max-w-xs">
                            <div
                              className="bg-gradient-to-r from-success-400 to-success-600 h-3 rounded-full transition-all duration-300"
                              style={{ width: `${deal.probability}%` }}
                            />
                          </div>
                          <span className="text-lg font-bold text-neutral-900">
                            {deal.probability}%
                          </span>
                        </div>
                      </dd>
                    </div>
                  )}
                  {deal.closeDate && (
                    <div>
                      <dt className="text-sm font-medium text-neutral-500 flex items-center">
                        <CalendarIcon className="h-4 w-4 mr-1" />
                        Expected Close Date
                      </dt>
                      <dd className="mt-2 text-sm font-medium text-neutral-900">
                        {new Date(deal.closeDate).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </dd>
                    </div>
                  )}
                  {deal.forecastCategory && (
                    <div>
                      <dt className="text-sm font-medium text-neutral-500">Forecast Category</dt>
                      <dd className="mt-2">
                        <Badge variant={
                          deal.forecastCategory === 'COMMIT' ? 'primary' :
                            deal.forecastCategory === 'BEST_CASE' ? 'secondary' :
                              deal.forecastCategory === 'PIPELINE' ? 'neutral' : 'warning'
                        }>
                          {deal.forecastCategory.replace('_', ' ')}
                        </Badge>
                      </dd>
                    </div>
                  )}
                </dl>
              </CardBody>
            </Card>
          )}

          {/* Order Processing Status */}
          {(deal.orderNumber || deal.paymentStatus || deal.shippingStatus) && (
            <Card variant="bordered">
              <CardHeader
                title="Order Processing Status"
                subtitle="Payment, fulfillment, and shipping information"
                icon={<ClipboardDocumentListIcon className="h-5 w-5" />}
              />
              <CardBody>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Order Info */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-neutral-700 flex items-center">
                      <DocumentTextIcon className="h-4 w-4 mr-2" />
                      Order Information
                    </h4>
                    {deal.orderNumber && (
                      <div>
                        <dt className="text-xs text-neutral-500">Order Number</dt>
                        <dd className="text-sm font-mono font-semibold text-neutral-900">{deal.orderNumber}</dd>
                      </div>
                    )}
                    {deal.sourceChannel && (
                      <div>
                        <dt className="text-xs text-neutral-500">Source Channel</dt>
                        <dd className="text-sm text-neutral-900">{formatStatus(deal.sourceChannel)}</dd>
                      </div>
                    )}
                    {deal.poNumber && (
                      <div>
                        <dt className="text-xs text-neutral-500">PO Number</dt>
                        <dd className="text-sm text-neutral-900">{deal.poNumber}</dd>
                      </div>
                    )}
                    {deal.fulfillmentPriority && (
                      <div>
                        <dt className="text-xs text-neutral-500">Priority</dt>
                        <dd className="mt-1">
                          <Badge variant={getPriorityColor(deal.fulfillmentPriority)} size="sm">
                            {formatStatus(deal.fulfillmentPriority)}
                          </Badge>
                        </dd>
                      </div>
                    )}
                  </div>

                  {/* Payment Info */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-neutral-700 flex items-center">
                      <BanknotesIcon className="h-4 w-4 mr-2" />
                      Payment
                    </h4>
                    {deal.paymentStatus && (
                      <div>
                        <dt className="text-xs text-neutral-500">Payment Status</dt>
                        <dd className="mt-1">
                          <Badge variant={getPaymentStatusColor(deal.paymentStatus)} size="sm">
                            {formatStatus(deal.paymentStatus)}
                          </Badge>
                        </dd>
                      </div>
                    )}
                    {deal.paymentType && (
                      <div>
                        <dt className="text-xs text-neutral-500">Payment Terms</dt>
                        <dd className="text-sm text-neutral-900">{formatStatus(deal.paymentType)}</dd>
                      </div>
                    )}
                    {deal.paymentDueDate && (
                      <div>
                        <dt className="text-xs text-neutral-500">Due Date</dt>
                        <dd className="text-sm text-neutral-900">
                          {new Date(deal.paymentDueDate).toLocaleDateString()}
                        </dd>
                      </div>
                    )}
                  </div>

                  {/* Shipping Info */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-neutral-700 flex items-center">
                      <TruckIcon className="h-4 w-4 mr-2" />
                      Shipping
                    </h4>
                    {deal.shippingStatus && (
                      <div>
                        <dt className="text-xs text-neutral-500">Shipping Status</dt>
                        <dd className="mt-1">
                          <Badge variant={getShippingStatusColor(deal.shippingStatus)} size="sm">
                            {formatStatus(deal.shippingStatus)}
                          </Badge>
                        </dd>
                      </div>
                    )}
                    {deal.shippingCarrier && (
                      <div>
                        <dt className="text-xs text-neutral-500">Carrier</dt>
                        <dd className="text-sm text-neutral-900">{formatStatus(deal.shippingCarrier)}</dd>
                      </div>
                    )}
                    {deal.trackingNumber && (
                      <div>
                        <dt className="text-xs text-neutral-500">Tracking Number</dt>
                        <dd className="text-sm font-mono text-neutral-900">
                          {deal.trackingUrl ? (
                            <a href={deal.trackingUrl} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                              {deal.trackingNumber}
                            </a>
                          ) : (
                            deal.trackingNumber
                          )}
                        </dd>
                        <button
                          onClick={() => navigator.clipboard?.writeText(deal.trackingNumber || '')}
                          className="mt-1 text-xs font-semibold text-primary-600 hover:underline"
                        >
                          Copy tracking number
                        </button>
                      </div>
                    )}
                    {deal.shippedAt && (
                      <div>
                        <dt className="text-xs text-neutral-500">Shipped</dt>
                        <dd className="text-sm text-neutral-900">{new Date(deal.shippedAt).toLocaleString()}</dd>
                      </div>
                    )}
                    {deal.targetShipDate && (
                      <div>
                        <dt className="text-xs text-neutral-500">Target Ship Date</dt>
                        <dd className="text-sm text-neutral-900">
                          {new Date(deal.targetShipDate).toLocaleDateString()}
                        </dd>
                      </div>
                    )}
                    {deal.isInternational && (
                      <div>
                        <Badge variant="primary" size="sm">International Shipment</Badge>
                      </div>
                    )}
                  </div>
                </div>

                {/* Special Instructions */}
                {deal.specialInstructions && (
                  <div className="mt-6 pt-6 border-t border-neutral-200">
                    <h4 className="text-sm font-semibold text-neutral-700 mb-2">Special Instructions</h4>
                    <p className="text-sm text-neutral-600 whitespace-pre-wrap">{deal.specialInstructions}</p>
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {/* Order Items */}
          {deal.orderItems && deal.orderItems.length > 0 && (
            <Card variant="bordered">
              <CardHeader
                title="Order Items"
                subtitle="Itemized list of products/services in this deal"
                icon={<ShoppingCartIcon className="h-5 w-5" />}
              />
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-neutral-200">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Item Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        SKU
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Subtotal
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-neutral-200">
                    {deal.orderItems.map((item, index) => (
                      <tr key={index} className="hover:bg-neutral-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900">
                          {item.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
                          {item.sku || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 text-right">
                          {item.quantity}{item.unit || item.product?.unit ? ` ${item.unit || item.product?.unit}` : ''}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 text-right">
                          {deal.currency} {Number(item.price).toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900 text-right">
                          {deal.currency} {(Number(item.price) * item.quantity).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-neutral-50">
                    <tr>
                      <td colSpan={4} className="px-6 py-4 text-right text-sm font-semibold text-neutral-900">
                        Total Amount:
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-primary-600 text-right">
                        {formatCurrency(deal.amount, deal.currency)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </Card>
          )}

          <Card variant="bordered" className="overflow-hidden">
            <div className="border-b border-neutral-200 bg-neutral-50 px-6 py-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="flex items-center text-lg font-semibold text-neutral-900">
                    <DocumentTextIcon className="mr-2 h-5 w-5 text-primary-600" />
                    Customer Purchase Orders
                  </h3>
                  <p className="mt-1 text-sm text-neutral-600">
                    Customer PO files attached to this deal for accounting and product traceability.
                  </p>
                </div>
                <Button
                  variant="primary"
                  onClick={() => setShowPoUploadForm((current) => !current)}
                  leftIcon={<DocumentArrowUpIcon className="h-4 w-4" />}
                >
                  Upload PO
                </Button>
              </div>
            </div>

            <CardBody className="p-6">
              {showPoUploadForm && (
                <form onSubmit={handleUploadDealPo} className="mb-6 rounded-lg border border-primary-200 bg-primary-50 p-4">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700">PO Name</label>
                      <input
                        type="text"
                        value={dealPoName}
                        onChange={(event) => setDealPoName(event.target.value)}
                        placeholder="Defaults to file name"
                        className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700">File *</label>
                      <input
                        type="file"
                        required
                        onChange={(event) => setDealPoFile(event.target.files?.[0] || null)}
                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.xlsx,.xls"
                        className="mt-1 block w-full text-sm text-neutral-500 file:mr-3 file:rounded-md file:border-0 file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary-700 hover:file:bg-primary-100"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-neutral-700">Description</label>
                      <textarea
                        value={dealPoDescription}
                        onChange={(event) => setDealPoDescription(event.target.value)}
                        rows={2}
                        className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowPoUploadForm(false);
                        setDealPoFile(null);
                        setDealPoName('');
                        setDealPoDescription('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" variant="primary" isLoading={uploadingDealPo} disabled={!dealPoFile}>
                      Upload Customer PO
                    </Button>
                  </div>
                </form>
              )}

              {customerPurchaseOrders.length === 0 ? (
                <EmptyState
                  icon={<DocumentTextIcon className="h-12 w-12" />}
                  title="No customer POs"
                  description="Upload the customer's purchase order here or attach it while generating an invoice."
                />
              ) : (
                <div className="overflow-x-auto rounded-lg border border-neutral-200">
                  <table className="min-w-full divide-y divide-neutral-200">
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-neutral-500">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-neutral-500">Uploaded</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-neutral-500">Size</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-neutral-500">Description</th>
                        <th className="px-4 py-3 text-right text-xs font-medium uppercase text-neutral-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100 bg-white">
                      {customerPurchaseOrders.map((document) => (
                        <tr key={document.id}>
                          <td className="px-4 py-3 text-sm font-medium text-neutral-900">{document.name}</td>
                          <td className="px-4 py-3 text-sm text-neutral-700">{formatDate(document.uploadedAt)}</td>
                          <td className="px-4 py-3 text-sm text-neutral-700">{formatFileSize(document.fileSize)}</td>
                          <td className="px-4 py-3 text-sm text-neutral-700">{document.description || '-'}</td>
                          <td className="px-4 py-3 text-right">
                            <div className="inline-flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => dealsAPI.downloadDocument(deal.id, document.id, document.name)}
                                className="rounded-md p-1.5 text-primary-600 transition-colors hover:bg-primary-50 hover:text-primary-800"
                                title="Download PO"
                              >
                                <DocumentArrowDownIcon className="h-5 w-5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteOrderDocument(document.id)}
                                className="rounded-md p-1.5 text-error-600 transition-colors hover:bg-error-50 hover:text-error-800"
                                title="Delete PO"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Problems & Issues Section */}
          <Card variant="bordered">
            <CardBody className="p-6">
              <ProblemsSection dealId={deal.id} />
            </CardBody>
          </Card>

          {/* Quotes Section */}
          <Card variant="bordered" className="overflow-hidden">
            <div className="px-6 py-5 border-b border-neutral-200 bg-gradient-to-r from-secondary-50 to-primary-50">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900 flex items-center">
                    <DocumentDuplicateIcon className="h-5 w-5 mr-2 text-secondary-600" />
                    Quotes & Proposals
                  </h3>
                  <p className="mt-1 text-sm text-neutral-600">
                    Manage quotes and proposals for this deal
                  </p>
                </div>
                <Button variant="primary" onClick={() => setShowQuoteModal(true)}>
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Create Quote
                </Button>
              </div>
            </div>

            <div className="px-6 py-5">
              {loadingQuotes ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-600"></div>
                  <p className="mt-2 text-sm text-neutral-500">Loading quotes...</p>
                </div>
              ) : quotes.length === 0 ? (
                <div className="text-center py-12">
                  <DocumentDuplicateIcon className="mx-auto h-12 w-12 text-neutral-400" />
                  <h3 className="mt-2 text-sm font-medium text-neutral-900">No quotes yet</h3>
                  <p className="mt-1 text-sm text-neutral-500">
                    Get started by creating a new quote for this deal.
                  </p>
                  <div className="mt-6">
                    <Link
                      to={getDealPath(`/deals/${deal.id}/quotes/new`)}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-secondary-600 to-primary-600 hover:from-secondary-700 hover:to-primary-700 shadow-sm"
                    >
                      <PlusIcon className="h-4 w-4 mr-1" />
                      Create First Quote
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {quotes.map((quote) => (
                    <div
                      key={quote.id}
                      className="border border-neutral-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200 bg-gradient-to-r from-white to-neutral-50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <h4 className="text-base font-semibold text-neutral-900">
                              {quote.quoteNumber}
                            </h4>
                            {getQuoteStatusBadge(quote.status)}
                            {quote.revisionNumber > 1 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-secondary-100 text-secondary-800">
                                Rev {quote.revisionNumber}
                              </span>
                            )}
                          </div>

                          {quote.title && (
                            <p className="mt-1 text-sm text-neutral-600">{quote.title}</p>
                          )}

                          <div className="mt-3 grid grid-cols-2 gap-4">
                            <div>
                              <dt className="text-xs font-medium text-neutral-500">Total Amount</dt>
                              <dd className="mt-1 text-lg font-bold text-secondary-600">
                                {quote.currency} {Number(quote.total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-xs font-medium text-neutral-500">Items</dt>
                              <dd className="mt-1 text-sm text-neutral-900">
                                {quote.items?.length || 0} item{quote.items?.length !== 1 ? 's' : ''}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-xs font-medium text-neutral-500">Created</dt>
                              <dd className="mt-1 text-sm text-neutral-900">
                                {new Date(quote.createdAt).toLocaleDateString()}
                              </dd>
                            </div>
                            {quote.validUntil && (
                              <div>
                                <dt className="text-xs font-medium text-neutral-500">Valid Until</dt>
                                <dd className="mt-1 text-sm text-neutral-900">
                                  {new Date(quote.validUntil).toLocaleDateString()}
                                </dd>
                              </div>
                            )}
                          </div>

                          {quote.viewCount > 0 && (
                            <div className="mt-2 flex items-center text-xs text-neutral-500">
                              <EyeIcon className="h-4 w-4 mr-1" />
                              Viewed {quote.viewCount} time{quote.viewCount !== 1 ? 's' : ''}
                              {quote.viewedAt && ` • Last viewed ${new Date(quote.viewedAt).toLocaleDateString()}`}
                            </div>
                          )}
                        </div>

                        <div className="ml-4 flex flex-col space-y-2">
                          <button
                            onClick={() => setSelectedQuoteId(quote.id)}
                            className="inline-flex items-center px-3 py-1.5 border border-neutral-300 text-xs font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50"
                          >
                            <EyeIcon className="h-3 w-3 mr-1" />
                            View
                          </button>
                          {quote.status === 'DRAFT' && (
                            <button
                              onClick={() => setQuoteToSend(quote)}
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                            >
                              <PaperAirplaneIcon className="h-3 w-3 mr-1" />
                              Send
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>

          {/* Details */}
          {deal.details && (
            <Card variant="bordered">
              <CardHeader
                title="Deal Details & Specifications"
                subtitle="Comprehensive information about this deal"
                icon={<DocumentTextIcon className="h-5 w-5" />}
              />
              <CardBody>
                <div className="prose prose-sm max-w-none">
                  <p className="text-neutral-900 whitespace-pre-wrap leading-relaxed">{deal.details}</p>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Notes */}
          {deal.notes && (
            <Card variant="bordered">
              <CardHeader title="Notes" icon={<DocumentTextIcon className="h-5 w-5" />} />
              <CardBody>
                <p className="text-sm text-neutral-900 whitespace-pre-wrap">{deal.notes}</p>
              </CardBody>
            </Card>
          )}

          {/* Tasks Section */}
          <Card variant="bordered" className="overflow-hidden">
            <div className="px-6 py-5 border-b border-neutral-200 bg-gradient-to-r from-primary-50 to-secondary-50">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900 flex items-center">
                    <CheckCircleIcon className="h-5 w-5 mr-2 text-primary-600" />
                    Tasks
                  </h3>
                  <p className="mt-1 text-sm text-neutral-600">
                    All tasks related to this deal
                  </p>
                </div>
                <Button variant="primary" onClick={() => setShowTaskModal(true)}>
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Task
                </Button>
              </div>
            </div>

            <div className="px-6 py-5">
              <TaskList
                key={taskRefreshKey}
                dealId={Number(id)}
                showFilters={false}
              />
            </div>
          </Card>
        </div>

        {/* Client Information */}
        <div className="lg:col-span-1">
          <Card variant="bordered">
            <CardHeader title="Client Information" icon={<BuildingOfficeIcon className="h-5 w-5" />} />
            <CardBody>
              {deal.client ? (
                <div className="space-y-4">
                  <div>
                    <dt className="text-sm font-medium text-neutral-500">Name</dt>
                    <dd className="mt-1 text-sm text-neutral-900">
                      <Link
                        to={getDealPath(`/clients/${deal.client.id}`)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        {deal.client.name}
                      </Link>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-neutral-500">Type</dt>
                    <dd className="mt-1">
                      <Badge variant="neutral">{deal.client.type}</Badge>
                    </dd>
                  </div>
                  {deal.client.email && (
                    <div>
                      <dt className="text-sm font-medium text-neutral-500">Email</dt>
                      <dd className="mt-1 text-sm text-neutral-900">
                        <a href={`mailto:${deal.client.email}`} className="text-primary-600 hover:text-primary-900">
                          {deal.client.email}
                        </a>
                      </dd>
                    </div>
                  )}
                  {deal.client.phoneNumbers && deal.client.phoneNumbers.length > 0 && (
                    <div>
                      <dt className="text-sm font-medium text-neutral-500">Phone</dt>
                      <dd className="mt-1 text-sm text-neutral-900">
                        {deal.client.phoneNumbers.map((phone: string, index: number) => (
                          <div key={index}>
                            <a href={`tel:${phone}`} className="text-primary-600 hover:text-primary-900">
                              {phone}
                            </a>
                          </div>
                        ))}
                      </dd>
                    </div>
                  )}
                  {deal.client.address && (
                    <div>
                      <dt className="text-sm font-medium text-neutral-500">Address</dt>
                      <dd className="mt-1 text-sm text-neutral-900">{deal.client.address}</dd>
                    </div>
                  )}
                </div>
              ) : (
                <EmptyState
                  type="clients"
                  title="No client"
                  description="Client information not available"
                />
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-neutral-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <h3 className="text-lg font-medium text-neutral-900">Delete Deal</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-neutral-500">
                  Are you sure you want to delete this deal? This action cannot be undone.
                </p>
              </div>
              <div className="flex justify-center space-x-3 pt-4">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 bg-neutral-300 text-neutral-800 text-sm font-medium rounded-md hover:bg-neutral-400"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-error-600 text-white text-sm font-medium rounded-md hover:bg-error-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <InvoiceGeneratorModal
        isOpen={showInvoiceModal}
        onClose={() => setShowInvoiceModal(false)}
        deals={[deal]}
        companies={companies}
        initialDealId={deal.id}
        lockDeal
        onSuccess={handleInvoiceSuccess}
      />

      {/* Quote Modal */}
      {showQuoteModal && deal && (
        <QuoteModal
          deal={deal}
          onClose={() => setShowQuoteModal(false)}
          onSuccess={() => {
            fetchQuotes();
            setShowQuoteModal(false);
          }}
        />
      )}

      {/* Quote Detail Modal */}
      {selectedQuoteId && (
        <QuoteDetailModal
          quoteId={selectedQuoteId}
          onClose={() => setSelectedQuoteId(null)}
          onRefresh={() => {
            fetchQuotes();
            setSelectedQuoteId(null);
          }}
        />
      )}

      <QuoteSendModal
        quote={quoteToSend}
        onClose={() => setQuoteToSend(null)}
        onSent={fetchQuotes}
      />

      {/* Task Form Modal */}
      {showTaskModal && (
        <TaskFormModal
          onClose={() => setShowTaskModal(false)}
          onSuccess={() => {
            setTaskRefreshKey(prev => prev + 1);
            setShowTaskModal(false);
          }}
          defaultDealId={Number(id)}
          defaultClientId={deal?.clientId}
        />
      )}

      {/* Order Receipt Modal */}
      {showReceiptModal && deal && (
        <OrderReceiptModal
          deal={deal}
          onClose={() => setShowReceiptModal(false)}
        />
      )}
      {deal && (
        <ShippingTrackingModal
          deal={deal}
          isOpen={showShippingModal}
          onClose={() => setShowShippingModal(false)}
          onSaved={(updated) => {
            setDeal(updated);
            setMessage('Shipping details saved.');
          }}
        />
      )}
    </div>
  );
};

export default DealDetail;
